/**
 * Audit Stage Controller: client-side pacer.
 *
 * The Restaurant Audit show page renders all six
 * step visuals into the DOM up front, hidden by
 * default via CSS. This controller toggles which
 * one is visible (the "stage") and listens for
 * advance signals.
 *
 * Server contract (CAL-264). Every step's canvas is
 * rendered ONCE on page load and is dataset-driven
 * by its own per-step Stimulus controller. The
 * server pushes payload changes via targeted
 * `set_dataset_attributes` Turbo Stream actions
 * directly on each canvas's wrapper. There is NO
 * canvas swap, NO pending slot, and no skeleton-vs-
 * revealed gating in this controller.
 *
 * Advance signals:
 *
 * - User clicks the Continue button → button
 *   dispatches `audit-continue:click` with
 *   `{fromKey}` → we advance to the next step.
 * - User acknowledges the report (clicks the
 *   bottom CTA on `reading_menu`) → server flips
 *   `reportAcknowledgedAt` on this controller's
 *   dataset → value-changed callback advances.
 * - Audit fails → freeze whatever is visible.
 *   The failure banner is rendered above the stage
 *   by the show view.
 */
import { Controller } from "@hotwired/stimulus";

const STEP_KEYS = [
  "locating",
  "reading_reviews",
  "picking_photo",
  "sampling_brand",
  "reading_menu",
  "painting_demo",
];

export default class extends Controller {
  static targets = ["stage"];

  static values = {
    locatingStatus: { type: String, default: "pending" },
    locatingFinishedAt: { type: String, default: "" },
    readingReviewsStatus: {
      type: String,
      default: "pending",
    },
    readingReviewsFinishedAt: {
      type: String,
      default: "",
    },
    pickingPhotoStatus: {
      type: String,
      default: "pending",
    },
    pickingPhotoFinishedAt: {
      type: String,
      default: "",
    },
    samplingBrandStatus: {
      type: String,
      default: "pending",
    },
    samplingBrandFinishedAt: {
      type: String,
      default: "",
    },
    readingMenuStatus: {
      type: String,
      default: "pending",
    },
    readingMenuFinishedAt: {
      type: String,
      default: "",
    },
    paintingDemoStatus: {
      type: String,
      default: "pending",
    },
    paintingDemoFinishedAt: {
      type: String,
      default: "",
    },
    auditStatus: { type: String, default: "pending" },
    reportAcknowledgedAt: {
      type: String,
      default: "",
    },
    // Endpoint we POST to on connect to backfill
    // any broadcasts that fired before the
    // browser's ActionCable subscription opened.
    replayStateUrl: { type: String, default: "" },
  };

  connect() {
    this.connected = true;
    this.replayObserver = null;
    this.replayTimeout = null;
    this.replayFired = false;
    this.targetsByKey = new Map();
    this.stageTargets.forEach((el) => {
      this.targetsByKey.set(
        el.dataset.auditStageKey,
        el
      );
    });

    this.auditId = this.extractAuditId();
    this.currentKey = null;

    const initialKey = this.pickInitialKey();
    this.showKey(initialKey);

    this.boundHandleContinueClick =
      this.handleContinueClick.bind(this);
    document.addEventListener(
      "audit-continue:click",
      this.boundHandleContinueClick
    );

    this.requestStateReplay();
  }

  disconnect() {
    this.connected = false;
    if (this.boundHandleContinueClick) {
      document.removeEventListener(
        "audit-continue:click",
        this.boundHandleContinueClick
      );
    }
    this.clearReplayWatch();
  }

  // -------- value-changed callbacks --------

  auditStatusValueChanged(value, prevValue) {
    if (!this.connected) return;
    if (value === prevValue) return;
    this.dispatchTransition();
  }

  reportAcknowledgedAtValueChanged(value, prevValue) {
    if (!this.connected) return;
    if (value === prevValue) return;
    if (!this.currentKey) return;
    if (this.currentKey !== "reading_menu") return;
    if (!value) return;
    this.advanceFrom("reading_menu");
  }

  // Per-step status callbacks: re-emit the
  // transition event so the rail / Continue button
  // can recompute UI state. The button itself
  // listens for `audit-step:complete` events
  // dispatched by each step's own canvas
  // controller; this transition is just for
  // visibility / failure handling.
  locatingStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }
  readingReviewsStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }
  pickingPhotoStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }
  samplingBrandStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }
  readingMenuStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }
  paintingDemoStatusValueChanged(v, p) {
    this.maybeDispatchTransition(v, p);
  }

  maybeDispatchTransition(value, prevValue) {
    if (!this.connected) return;
    if (value === prevValue) return;
    this.dispatchTransition();
  }

  // -------- continue button --------

  handleContinueClick(event) {
    const fromKey = event.detail?.fromKey;
    if (!fromKey) return;
    if (this.currentKey !== fromKey) return;
    this.advanceFrom(fromKey);
  }

  // -------- stage transitions --------

  showKey(key) {
    if (!key) return;
    if (key === this.currentKey) return;

    this.targetsByKey.forEach((el, k) => {
      if (k === key) {
        el.setAttribute(
          "data-audit-stage-active",
          ""
        );
        el.dataset.auditStageActivatedAt =
          String(Date.now());
      } else {
        el.removeAttribute(
          "data-audit-stage-active"
        );
      }
    });

    this.currentKey = key;
    this.dispatchTransition();
  }

  advanceFrom(key) {
    const idx = STEP_KEYS.indexOf(key);
    if (idx < 0 || idx >= STEP_KEYS.length - 1) {
      return;
    }
    this.showKey(STEP_KEYS[idx + 1]);
  }

  // -------- transition event for sibling rail --------

  dispatchTransition() {
    if (!this.connected) return;
    this.element.dispatchEvent(
      new CustomEvent("audit-stage:transition", {
        bubbles: true,
        detail: {
          currentKey: this.currentKey,
          currentStepStatus: this.currentKey
            ? this.statusFor(this.currentKey)
            : null,
          auditStatus: this.auditStatusValue,
          reportAcknowledgedAt:
            this.reportAcknowledgedAtValue,
        },
      })
    );
  }

  // -------- initial-paint decision --------

  pickInitialKey() {
    if (
      this.auditStatusValue === "succeeded" &&
      this.reportAcknowledgedAtValue
    ) {
      return "painting_demo";
    }

    // Earliest step that's running, or the report
    // if the user hasn't acknowledged yet, otherwise
    // the latest done step.
    for (const key of STEP_KEYS) {
      const status = this.statusFor(key);
      if (status === "running") return key;
      if (status === "done") {
        if (
          key === "reading_menu" &&
          !this.reportAcknowledgedAtValue
        ) {
          return key;
        }
        if (key === "painting_demo") {
          if (this.reportAcknowledgedAtValue) {
            return key;
          }
          continue;
        }
      }
    }

    for (let i = STEP_KEYS.length - 1; i >= 0; i--) {
      const key = STEP_KEYS[i];
      if (this.statusFor(key) === "done") return key;
    }

    return "locating";
  }

  // -------- broadcast resync --------

  requestStateReplay() {
    const url = this.replayStateUrlValue;
    if (!url) return;

    const source = this.findStreamSource();

    if (!source) {
      this.fireReplay();
      return;
    }

    if (source.hasAttribute("connected")) {
      this.fireReplay();
      return;
    }

    this.replayObserver = new MutationObserver(() => {
      if (!source.hasAttribute("connected")) return;
      this.clearReplayWatch();
      this.fireReplay();
    });
    this.replayObserver.observe(source, {
      attributes: true,
      attributeFilter: ["connected"],
    });

    this.replayTimeout = setTimeout(() => {
      this.replayTimeout = null;
      this.clearReplayWatch();
      this.fireReplay();
    }, 3_000);
  }

  fireReplay() {
    if (this.replayFired) return;
    this.replayFired = true;

    const url = this.replayStateUrlValue;
    if (!url) return;

    const csrf = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrf || "",
        Accept: "text/vnd.turbo-stream.html",
      },
      credentials: "same-origin",
    }).catch(() => {});
  }

  findStreamSource() {
    return document.querySelector(
      "turbo-cable-stream-source"
    );
  }

  clearReplayWatch() {
    if (this.replayObserver) {
      this.replayObserver.disconnect();
      this.replayObserver = null;
    }
    if (this.replayTimeout) {
      clearTimeout(this.replayTimeout);
      this.replayTimeout = null;
    }
  }

  // -------- DOM helpers --------

  extractAuditId() {
    const match = this.element.id.match(
      /^audit-stage-(.+)$/
    );
    return match ? match[1] : null;
  }

  // -------- value accessors keyed by step --------

  statusFor(key) {
    switch (key) {
      case "locating":
        return this.locatingStatusValue;
      case "reading_reviews":
        return this.readingReviewsStatusValue;
      case "picking_photo":
        return this.pickingPhotoStatusValue;
      case "sampling_brand":
        return this.samplingBrandStatusValue;
      case "reading_menu":
        return this.readingMenuStatusValue;
      case "painting_demo":
        return this.paintingDemoStatusValue;
      default:
        return "pending";
    }
  }

  finishedAtFor(key) {
    switch (key) {
      case "locating":
        return this.locatingFinishedAtValue;
      case "reading_reviews":
        return this.readingReviewsFinishedAtValue;
      case "picking_photo":
        return this.pickingPhotoFinishedAtValue;
      case "sampling_brand":
        return this.samplingBrandFinishedAtValue;
      case "reading_menu":
        return this.readingMenuFinishedAtValue;
      case "painting_demo":
        return this.paintingDemoFinishedAtValue;
      default:
        return "";
    }
  }
}
